import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../../../../../../components/Modal';
import { CustomButton } from '../../../../../../components/Button/Button';
import CloseButton from '../../../../../../components/CloseButton/CloseButton';
import Toggle from '../../../../../../components/Toggle/Toggle';
import Icon from '../../../../../../components/Icon/Icon';
import styles from './EditFeatureModal.module.scss';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

const EditFeatureModal = ({ open, featureData, onClose, onSave, isCreateMode, hideRename }) => {
    const [editName, setEditName] = useState(false);
    const [name, setName] = useState('');
    const [publicName, setPublicName] = useState('');
    const [userOverridable, setUserOverridable] = useState(false);
    const [isDefault, setIsDefault] = useState(false);
    const [defaultValue, setDefaultValue] = useState('');
    const [isChanged, setIsChanged] = useState(false);
    const inputRef = useRef(null);

    const { t } = useTranslation();

    useEffect(() => {
        if (featureData) {
            setName(featureData.name || featureData.feature?.name || '');
            setPublicName(featureData.publicName || featureData.feature?.publicName || '');
            setUserOverridable(
                featureData.userOverridable !== undefined ? !!featureData.userOverridable :
                    featureData.feature?.userOverridable !== undefined ? !!featureData.feature.userOverridable :
                        false
            );
            setIsDefault(
                featureData.default !== undefined ? !!featureData.default :
                    featureData.enabled !== undefined ? !!featureData.enabled :
                        featureData.feature?.default !== undefined ? !!featureData.feature.default :
                            false
            );
            setDefaultValue(
                featureData.defaultValue ||
                featureData.value ||
                featureData.feature?.defaultValue ||
                ''
            );
            setEditName(false);
            setIsChanged(false);
        }
    }, [featureData]);

    const handleSave = () => {
        onSave({
            ...featureData,
            name,
            publicName,
            userOverridable,
            enabled: isDefault ? 1 : 0,
            value: defaultValue,
            default: isDefault ? 1 : 0,
            defaultValue: defaultValue,
        });
        setIsChanged(false);
        onClose();
    };

    const handleRenameClick = useCallback(() => {
        setEditName(true);
        setTimeout(() => {
            inputRef.current && inputRef.current.focus();
        }, 0);
    }, []);

    const handleNameChange = e => {
        setName(e.target.value);
        setIsChanged(true);
    };

    const handlePublicNameChange = e => {
        setPublicName(e.target.value);
        setIsChanged(true);
    };

    const handleUserOverridableToggle = () => {
        setUserOverridable(v => !v);
        setIsChanged(true);
    };

    const handleDefaultValueChange = e => {
        setDefaultValue(e.target.value);
        setIsChanged(true);
    };

    const handleToggleDefault = () => {
        setIsDefault(v => {
            const newValue = !v;
            if (!newValue) setUserOverridable(false);
            return newValue;
        });
        setIsChanged(true);
    };

    if (!featureData) return null;

    return (
        <Modal open={open} onClose={onClose} className={styles.modalWrapper} closeOnEsc={false}>
            <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <h2>{t(isCreateMode ? 'general.create' : 'general.edit')} feature</h2>
                    <CloseButton onClose={onClose} />
                </div>
                <div>
                    <div className={styles.nameWrapper}>
                        {hideRename ? (
                            <span className={styles.featureName}>{publicName || name}</span>
                        ) : editName ? (
                            <input
                                ref={inputRef}
                                className={styles.renameInput}
                                value={name}
                                onChange={handleNameChange}
                                maxLength={50}
                                onBlur={() => setEditName(false)}
                                autoFocus
                            />
                        ) : (
                            <>
                                <span className={styles.featureName}>{name}</span>
                                <CustomButton variant="link" className={styles.editBtn} onClick={handleRenameClick}>
                                    <Icon src="pencil" className={styles.pencilIcon} />
                                </CustomButton>
                            </>
                        )}
                    </div>
                    {!hideRename && (
                        <>
                            <div className={styles.formGroup}>
                                <label>{t('company.settings.features.public_name')}</label>
                                <textarea
                                    type="text"
                                    className={styles.nameInput}
                                    value={publicName}
                                    rows={1}
                                    onChange={handlePublicNameChange}
                                    maxLength={100}
                                />
                            </div>
                        </>
                    )}
                    <div className={styles.isDefaultContainer}>
                        <Toggle id={t('company.settings.features.enabled')} checked={isDefault} onChange={handleToggleDefault} className={styles.toggle} />
                        <span>{hideRename ? t('company.settings.features.enabled') : t('company.settings.features.default')}</span>
                    </div>
                    {hideRename && (
                        <div className={cx(styles.isDefaultContainer, { [styles.disabled]: !isDefault })}>
                            <Toggle id={t('company.settings.features.user_overridable')} checked={userOverridable} onChange={handleUserOverridableToggle} disabled={!isDefault} className={styles.toggle} />
                            <span>{t('company.settings.features.user_overridable')}</span>
                        </div>
                    )}
                    <div className={styles.formGroup}>
                        <label>{t('company.settings.features.default_value')}</label>
                        <textarea
                            type="text"
                            className={styles.nameInput}
                            rows={1}
                            value={defaultValue}
                            onChange={handleDefaultValueChange}
                            maxLength={100}
                        />
                    </div>
                </div>
                <div className={styles.footerSeparator} />
                <div className={styles.buttonContainer}>
                    <CustomButton variant="secondary" label="Cancel" className={styles.controlBtn} onClick={onClose} />
                    <CustomButton
                        variant="primary"
                        label={t(isCreateMode ? 'general.create' : 'general.save')}
                        className={styles.controlBtn}
                        onClick={handleSave}
                        disabled={!isChanged}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default EditFeatureModal; 